/**
 * ManualLoginPage - magic-link manual-entry fallback at /login.
 *
 * Used when the patient arrives at the portal without clicking a link
 * (typed the URL from memory, lost the SMS, etc.). Two visible inputs:
 *   - Link code (token, typed by patient)
 *   - Date of birth
 *
 * Submit hits the same endpoint as MagicLinkLandingPage, navigating
 * via `redirect_path` on success.
 *
 * Per decision ledger #43: this page replaces the PIN+DOB LoginPage at
 * `/login` when MAGIC_LINK_ENABLED is true. The old LoginPage is kept
 * in the source tree for the flag-off path during Phase A migration;
 * AppRouter chooses one or the other based on the flag.
 *
 * "Lost your code?" copy per design §8.4: no self-service reissue;
 * patient must contact the clinic.
 */
import { useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

import { useAuth } from '../../auth/AuthContext';
import { sanitizeNext } from '../../auth/sanitizeNext';
import type { MagicLinkResponse } from '../../api/types';
import { MagicLinkDobForm } from './MagicLinkDobForm';
import { PIN_LOGIN_ENABLED } from '../../lib/featureFlags';
import { LOGIN_THEME, CURRENT_BRAND } from '../../lib/loginTheme';
import { PrimroseLoginShell } from './shells/PrimroseLoginShell';
import { IrisLoginShell } from './shells/IrisLoginShell';

// Pick the brand shell once at module load -- VITE_LOGIN_THEME is a
// build-time constant so this never changes at runtime.
const LoginShell = LOGIN_THEME === 'iris' ? IrisLoginShell : PrimroseLoginShell;

export function ManualLoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const auth = useAuth();

  // If the user is already authenticated (e.g. they hit /login while a
  // valid session exists), forward them straight to the next destination.
  // Same pattern as the old LoginPage.
  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate(sanitizeNext(params.get('next')), { replace: true });
    }
  }, [auth.isAuthenticated, navigate, params]);

  const onSuccess = (response: MagicLinkResponse) => {
    auth.refresh();
    // Prefer the backend's redirect_path over the ?next= param. The
    // backend knows the token's scope (patient vs appointment) and
    // routes accordingly; ?next= is a hint at best and could try to
    // route an appointment-scope session to /pay, which would 401
    // with APPOINTMENT_SCOPE_VIOLATION. Backend > ?next=.
    navigate(response.redirect_path, { replace: true });
  };

  return (
    <LoginShell>
      <div className={LOGIN_THEME === 'iris' ? 'ir-explainer' : 'pr-explainer'}>
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
          <circle cx="10" cy="10" r="8" />
          <path d="M10 9v4M10 6.5v.4" />
        </svg>
        <span>To protect your privacy, please enter your individual PIN and date of birth. We’ll display your bill only when both details match your provider’s records.</span>
      </div>

      <MagicLinkDobForm
        initialToken=""
        hideTokenInput={false}
        onSuccess={onSuccess}
      />
      {/*
        Lost-link recovery copy per design §8.4. No self-service
        re-issue -- patient must contact the clinic. Confirmed in
        design discussion as a deliberate choice (prevents
        self-service token enumeration attacks).
      */}
      <p className="mt-6 text-xs text-muted-foreground">
        Don&rsquo;t have a code? Please call your clinic to get a new
        link sent to your phone.
      </p>
      {/*
        v0.8.6: reciprocal dual-mode link. When PIN+DOB is also
        available, surface the secondary path. Most patients here
        already chose magic-link entry, but a few will have arrived
        from /login/code via the PIN-page link and realized they
        actually have a PIN -- give them a one-click way back.
        Per ledger #53.
      */}
      {PIN_LOGIN_ENABLED && (
        <p className="mt-3 text-xs text-muted-foreground">
          Have a PIN instead?{' '}
          <Link
            to="/login"
            className="font-medium text-primary underline-offset-2 hover:underline"
          >
            Enter it here
          </Link>
        </p>
      )}

      <p className={LOGIN_THEME === 'iris' ? 'ir-help' : 'pr-help'}>
        Can&rsquo;t find your PIN? Call billing at <a href={`tel:${CURRENT_BRAND.billingPhoneTel}`}>{CURRENT_BRAND.billingPhoneDisplay}</a>.
      </p>
    </LoginShell>
  );
}
